import PropTypes from 'prop-types'
import { useRef } from 'react'

import { useFrame } from '../engine'
import { anim, lerp, pv, seg, smooth } from '../motion'
import { COLORS } from '../primitives'
import { IsoBox, iso } from './iso'

const range = PropTypes.arrayOf(PropTypes.number)

export const INK = '#182033'

/**
 * Figur im Meeple-Stil, Ursprung ist die Fußmitte. `happy` ist der Bereich
 * von --l, in dem das neutrale Gesicht zu einem Lächeln wird. Optional
 * `sad` ([ein von, ein bis, aus von, aus bis]) für ein trauriges Gesicht.
 */
export function Person({ body, skin, hair, tie, happy, sad, sweat, label }) {
  const moods = sad && {
    ...pv('hm', happy),
    ...pv('sm', sad.slice(0, 2)),
    ...pv('so', sad.slice(2))
  }
  return (
    <g style={moods || undefined}>
      <ellipse cx='0' cy='0' rx='15' ry='6' fill='#C9D2E6' />
      <rect
        x='-12'
        y='-40'
        width='24'
        height='36'
        rx='11'
        fill={COLORS[body] || body}
      />
      {tie && (
        <path
          d='M-3 -36 L3 -36 L1.5 -22 L0 -19 L-1.5 -22 Z'
          fill={COLORS.yellow}
        />
      )}
      <circle cx='0' cy='-52' r='12' fill={skin} />
      {hair && (
        <path d='M-12 -53 A12 12 0 0 1 12 -53 Q0 -59 -12 -53 Z' fill={hair} />
      )}
      <circle cx='-4.2' cy='-52' r='1.7' fill={INK} />
      <circle cx='4.2' cy='-52' r='1.7' fill={INK} />
      <g
        className='off-a'
        style={
          sad
            ? {
                opacity:
                  'max(0, calc(1 - var(--hm) - var(--sm) * (1 - var(--so))))'
              }
            : anim({ at: [-1, 0], out: happy, y: 0, oy: 0 })
        }
      >
        <path
          d='M-3.5 -46.5 L3.5 -46.5'
          stroke={INK}
          strokeWidth='1.8'
          strokeLinecap='round'
        />
      </g>
      <g className='off-a' style={anim({ at: happy, y: 0, s: 0.4 })}>
        <path
          d='M-5 -47.5 Q0 -42.5 5 -47.5'
          fill='none'
          stroke={INK}
          strokeWidth='1.8'
          strokeLinecap='round'
        />
      </g>
      {sad && (
        <g style={{ opacity: 'calc(var(--sm) * (1 - var(--so)))' }}>
          <path
            d='M-5 -44.5 Q0 -49 5 -44.5'
            fill='none'
            stroke={INK}
            strokeWidth='1.8'
            strokeLinecap='round'
          />
        </g>
      )}
      {sweat && (
        <g
          className='off-a'
          style={anim({
            at: sweat.slice(0, 2),
            out: sweat.slice(2),
            y: -6,
            oy: 6
          })}
        >
          <path
            d='M14 -64 Q18 -57 14 -54 Q10 -57 14 -64 Z'
            fill={COLORS.blue}
          />
        </g>
      )}
      {label && (
        <text x='0' y='18' textAnchor='middle' className='off-name'>
          {label}
        </text>
      )}
    </g>
  )
}

Person.propTypes = {
  body: PropTypes.string.isRequired,
  skin: PropTypes.string.isRequired,
  hair: PropTypes.string,
  tie: PropTypes.bool,
  happy: range.isRequired,
  sad: range,
  sweat: range,
  label: PropTypes.string
}

/** Kleines Herz, das im Bereich `at` über einem Punkt aufploppt. */
export function Heart({ x, y, at, out, color = COLORS.pink }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className='off-a' style={anim({ at, out, y: 16, oy: -20, s: 0.2 })}>
        <path
          d='M0 4 C-8 -2 -9 -9 -4.5 -10 C-2 -10.5 -0.5 -9 0 -7.5 C0.5 -9 2 -10.5 4.5 -10 C9 -9 8 -2 0 4 Z'
          fill={color}
        />
      </g>
    </g>
  )
}

Heart.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  at: range.isRequired,
  out: range,
  color: PropTypes.string
}

export function Desk({
  x,
  y,
  w = 2.4,
  d = 1.3,
  tone = 'white',
  monitor = true
}) {
  const t =
    tone === 'wood'
      ? { top: '#FFE3A3', left: '#F2C770', right: '#E0B24F' }
      : { top: '#FFFFFF', left: '#DDE3EF', right: '#CBD3E3' }
  return (
    <g>
      <IsoBox x={x} y={y} w={w} d={d} h={1} {...t} />
      {monitor && (
        <IsoBox
          x={x + w * 0.35}
          y={y + 0.15}
          z={1}
          w={w * 0.4}
          d={0.12}
          h={0.7}
          top='#2B3654'
          left={INK}
          right='#2B3654'
        />
      )}
    </g>
  )
}

Desk.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  w: PropTypes.number,
  d: PropTypes.number,
  tone: PropTypes.oneOf(['white', 'wood']),
  monitor: PropTypes.bool
}

export function Plant({ x, y }) {
  const p = iso(x + 0.4, y + 0.4, 0.8)
  return (
    <g>
      <IsoBox
        x={x}
        y={y}
        w={0.8}
        d={0.8}
        h={0.8}
        top='#C7D2EA'
        left='#AEBBD8'
        right='#9DABCB'
      />
      <circle cx={p.x} cy={p.y - 16} r='15' fill='#2FC3A4' />
      <circle cx={p.x - 10} cy={p.y - 6} r='10' fill={COLORS.green} />
      <circle cx={p.x + 10} cy={p.y - 8} r='11' fill='#38CFAF' />
    </g>
  )
}

Plant.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired
}

export function Envelope() {
  return (
    <g>
      <rect
        x='-11'
        y='-7.5'
        width='22'
        height='15'
        rx='2'
        fill='#FFFFFF'
        stroke='#B6C3E0'
        strokeWidth='1.2'
      />
      <path
        d='M-11 -7 L0 1.5 L11 -7'
        fill='none'
        stroke='#B6C3E0'
        strokeWidth='1.2'
      />
      <circle cx='0' cy='1.5' r='2.6' fill={COLORS.pink} />
    </g>
  )
}

export function MiniPhone({ shape = 'tri', color = 'yellow' }) {
  return (
    <g>
      <rect x='-8' y='-14' width='16' height='28' rx='4' fill={INK} />
      <rect x='-6' y='-10' width='12' height='20' rx='2' fill='#FFFFFF' />
      {shape === 'tri' && <path d='M0 -5 L5 4 L-5 4 Z' fill={COLORS[color]} />}
      {shape === 'sq' && (
        <rect x='-4' y='-4' width='8' height='8' rx='1' fill={COLORS[color]} />
      )}
      {shape === 'dot' && <circle cx='0' cy='0' r='4.5' fill={COLORS[color]} />}
    </g>
  )
}

MiniPhone.propTypes = { shape: PropTypes.string, color: PropTypes.string }

/**
 * Fliegende Objekte. Jedes Item hat eine oder mehrere Etappen
 * ({ from, to, at, arc }) in Weltpixeln, `keep` lässt es am Ziel liegen.
 */
export function Flight({ items, render }) {
  const refs = useRef([])

  useFrame((l) => {
    items.forEach((item, i) => {
      const el = refs.current[i]
      if (!el) return
      const legs = item.legs
      const last = legs[legs.length - 1]
      if (l < legs[0].at[0] || (!item.keep && l > last.at[1])) {
        el.style.opacity = '0'
        return
      }
      let p = last.to
      let fade = 1
      let rot = item.rot || 0
      for (const leg of legs) {
        if (l <= leg.at[1]) {
          const t = smooth(seg(l, leg.at[0], leg.at[1]))
          p = {
            x: lerp(leg.from.x, leg.to.x, t),
            y:
              lerp(leg.from.y, leg.to.y, t) -
              Math.sin(Math.PI * t) * (leg.arc || 0)
          }
          rot = (item.rot || 0) + (leg.spin || 0) * t
          if (!item.keep && leg === last) fade = Math.min(1, (1 - t) / 0.2)
          if (leg.shrink) fade = Math.min(fade, 1 - t * 0.7)
          break
        }
      }
      el.style.opacity = String(fade)
      el.setAttribute(
        'transform',
        `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) rotate(${rot.toFixed(1)})`
      )
    })
  })

  return (
    <g>
      {items.map((item, i) => (
        <g key={i} ref={(el) => (refs.current[i] = el)} style={{ opacity: 0 }}>
          {render(item, i)}
        </g>
      ))}
    </g>
  )
}

Flight.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  render: PropTypes.func.isRequired
}

/**
 * Läuft eine Figur entlang von Wegpunkten ({ at, x, y } in Welteinheiten).
 * Zwischen zwei Wegpunkten wippt sie leicht wie beim Gehen.
 */
export function Walker({ path, children }) {
  const ref = useRef(null)

  useFrame((l) => {
    if (!ref.current) return
    let p = path[0]
    let bob = 0
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1]
      const b = path[i]
      if (l <= b.at) {
        const t = smooth(seg(l, a.at, b.at))
        p = { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) }
        const moving = t > 0 && t < 1 && (a.x !== b.x || a.y !== b.y)
        if (moving) bob = Math.abs(Math.sin(t * Math.PI * 7)) * 5
        break
      }
      p = b
    }
    const s = iso(p.x, p.y)
    ref.current.setAttribute(
      'transform',
      `translate(${s.x.toFixed(1)} ${(s.y - bob).toFixed(1)})`
    )
  })

  const start = iso(path[0].x, path[0].y)
  return (
    <g ref={ref} transform={`translate(${start.x} ${start.y})`}>
      {children}
    </g>
  )
}

Walker.propTypes = {
  path: PropTypes.arrayOf(
    PropTypes.shape({
      at: PropTypes.number,
      x: PropTypes.number,
      y: PropTypes.number
    })
  ).isRequired,
  children: PropTypes.node
}
